import { useState } from "react";

import { AccordionKeys } from "@shared";

import { DocumentGroup, counselorDocumentsApi } from "@/api";
import { MergeFilesDialog } from "@/common/components/layout/components/dropzone/components/merge-files-dialog/MergeFilesDialog";
import { useMergeFiles } from "@/common/components/layout/components/dropzone/components/merge-files-dialog/hooks/useMergeFiles";
import { UploadFormatDialog } from "@/common/components/layout/components/dropzone/components/upload-format-dialog/UploadFormatDialog";
import { useClientId } from "@/common/hooks/useClientId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { DropZoneSection } from "../dropzone-section/DropZoneSection";
import { useCounselorDropZone } from "./hooks/useCounselorDropZone";

export function CounselorDropZone() {
  const { t } = useTranslation();
  const clientId = useClientId();
  const {
    files,
    uploadingFiles,
    pendingIncomingMoves,
    getRootProps,
    getInputProps,
    isDragActive,
    selectedFiles,
    handleToggleSelect,
    fileActions,
    handleDelete,
    handleBulkDownload,
    isBulkActionPending,
    formatDialog,
  } = useCounselorDropZone(clientId);

  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const selectedFileList = files.filter((f) => selectedFiles.has(f.documentId));

  const mergeMutation = useMergeFiles({
    mergeFn: (orderedItems) =>
      counselorDocumentsApi.mergeDropZoneFiles({
        mergeCounselorDocumentsRequest: {
          items: orderedItems.map((it) => ({ documentId: it.fileId, rotationDegrees: it.rotationDegrees })),
        },
      }),
    invalidationKeys: [[QueryKey.DropZoneFiles]],
    onSuccess: () => setIsMergeOpen(false),
  });

  return (
    <>
      <DropZoneSection
        title={t("dropzone.panel.myUploads", {
          count: files.length + uploadingFiles.length + pendingIncomingMoves.length,
        })}
        accordionKey={AccordionKeys.dropzone.myUploads}
        isDragActive={isDragActive}
        getRootProps={getRootProps}
        getInputProps={getInputProps}
        files={files}
        uploadingFiles={uploadingFiles}
        pendingIncomingMoves={pendingIncomingMoves}
        selectedFiles={selectedFiles}
        onToggleSelect={handleToggleSelect}
        fileActions={fileActions}
        onBulkDownload={handleBulkDownload}
        onBulkDelete={() => handleDelete(selectedFileList)}
        onBulkMerge={() => setIsMergeOpen(true)}
        isBulkActionPending={isBulkActionPending}
        sourceZone={DocumentGroup.Counselor}
      />
      {isMergeOpen && selectedFileList.length >= 2 && (
        <MergeFilesDialog
          files={selectedFileList}
          onCancel={() => setIsMergeOpen(false)}
          onConfirm={(orderedItems) => mergeMutation.mutate(orderedItems)}
          isSubmitting={mergeMutation.isPending}
          fetchImageBlob={fileActions.onFetchDownloadBlob}
        />
      )}
      <UploadFormatDialog state={formatDialog} />
    </>
  );
}
